/**
 * client-preselect - the Workflow Hub `?client=` read, per the Hub client URL contract
 * (Wave 1a, "Hub Client URL Contract and Switchability List", 2026-09-04 6:00 PM, section 1).
 *
 * THE PARAMETER IS A PRESELECTION HINT FOR THE INTERFACE. It is not an authorization, not a data
 * root, and not a trusted value. Everything here is display-side: it decides which of the campaigns
 * this bench is CONFIGURED TO SERVE the pages ask the API for, and what the header says. The API
 * still derives the plan from the campaign, fail-closed, and every guard runs exactly as before.
 * Nothing in this module can make a record file under a different plan.
 *
 * Contract rules implemented, section 1.1 and 1.3:
 *   absent            -> "no opinion": the station's own default, no notice
 *   empty (?client=)  -> treated as absent
 *   repeated          -> INVALID, refused: default with a visible notice (never first, never last)
 *   unknown value     -> INVALID: default with a visible notice, never silently
 *   case              -> lowercased before comparing (the Hub emits lowercase)
 *   known plan        -> preselect ONLY when exactly one of this station's configured campaigns
 *                        belongs to that plan. None: default with a notice naming both plans.
 *                        More than one: default with a notice; nothing is picked automatically.
 *   echo              -> the header shows the plan and campaign actually landed on, and says
 *                        "from the Hub" when the parameter chose it
 *   never cross-plan  -> the default is the station's default, shown as itself, with the notice
 *                        saying so; a refused parameter never lands anywhere else
 *
 * Why the value is carried in sessionStorage: sign-in is a redirect (MSAL loginRedirect, and the
 * bench idle-logs-off every 15 minutes), and the return navigation lands on /login/callback, not on
 * the Hub's landing URL, so the parameter would be lost on the very first click of the day.
 * captureHubClientParam runs at boot, before any redirect, and stores the RAW values (all of them,
 * so a repeated parameter is still refused after the round trip). Tab-scoped, gone on tab close,
 * exactly like the MSAL cache. Section 1.3.4 allows adopting the value into station state.
 */
import { CAMPAIGN_CLIENT, CLIENT_LABELS, clientLabel, isClientKey, type ClientKey } from './client-key';

export const HUB_CLIENT_PARAM = 'client';
export const HUB_CLIENT_STORAGE_KEY = 'li.hubClient';

/** The campaign every page asks for when nothing preselects another. */
export const DEFAULT_CAMPAIGN = '26fobt';

/**
 * The campaigns this bench is configured to serve. ONE today. Adding a plan's campaign slug here
 * (once that plan has a campaign database the API can open) is what lets a Hub `?client=` link
 * preselect it; until then the link lands on the default with a notice, never on a guess.
 */
export const STATION_CAMPAIGNS: readonly string[] = ['26fobt'];

/** Display name per campaign slug, for the header. Unknown slugs show as themselves. */
export const CAMPAIGN_DISPLAY: Readonly<Record<string, string>> = { '26fobt': '26 FOBT' };

export function campaignDisplay(campaign: string): string {
  return CAMPAIGN_DISPLAY[campaign] ?? campaign;
}

export type PreselectSource = 'default' | 'hub';

export interface ClientPreselect {
  /** The campaign the pages ask the API for. */
  campaign: string;
  /** The plan that owns that campaign (from the compiled map), or null if the map does not know it. */
  client: ClientKey | null;
  /** 'hub' when the `?client=` parameter chose the campaign; 'default' otherwise. */
  source: PreselectSource;
  /** Visible text when the parameter was present but could not be honoured. Null when nothing to say. */
  notice: string | null;
}

export interface PreselectOptions {
  stationCampaigns?: readonly string[];
  defaultCampaign?: string;
}

/** Every raw value of the `client` parameter, in order, exactly as given (no trimming, no lowercasing). */
export function hubClientValues(search: string): string[] {
  try {
    return new URLSearchParams(search ?? '').getAll(HUB_CLIENT_PARAM);
  } catch {
    return [];
  }
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function storage(explicit?: StorageLike): StorageLike | null {
  if (explicit) return explicit;
  try {
    return typeof window !== 'undefined' && window.sessionStorage ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

/**
 * At boot: if the landing URL carries the parameter, remember its raw values for this tab so they
 * survive the sign-in redirect. A URL WITHOUT the parameter leaves any remembered value alone (the
 * bench user navigates between tabs by path all day; that is not the Hub changing its mind).
 */
export function captureHubClientParam(search: string, store?: StorageLike): void {
  const values = hubClientValues(search);
  if (values.length === 0) return;
  const s = storage(store);
  if (!s) return;
  try {
    s.setItem(HUB_CLIENT_STORAGE_KEY, JSON.stringify(values));
  } catch {
    /* storage unavailable (private mode, quota): the URL read still works on this render */
  }
}

/** The values remembered by captureHubClientParam for this tab, or null when none. */
export function recallHubClientValues(store?: StorageLike): string[] | null {
  const s = storage(store);
  if (!s) return null;
  try {
    const raw = s.getItem(HUB_CLIENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed) || !parsed.every((x) => typeof x === 'string')) return null;
    return parsed as string[];
  } catch {
    return null;
  }
}

/** Forget the remembered parameter (a station control taking over, or a test). */
export function clearHubClientParam(store?: StorageLike): void {
  const s = storage(store);
  if (!s) return;
  try {
    s.removeItem(HUB_CLIENT_STORAGE_KEY);
  } catch {
    /* nothing to do */
  }
}

function defaultPreselect(defaultCampaign: string, notice: string | null): ClientPreselect {
  return {
    campaign: defaultCampaign,
    client: CAMPAIGN_CLIENT[defaultCampaign] ?? null,
    source: 'default',
    notice,
  };
}

/**
 * The decision, from the raw parameter values. Pure; every rule in the file header lives here.
 */
export function resolveClientPreselect(values: readonly string[], opts: PreselectOptions = {}): ClientPreselect {
  const stationCampaigns = opts.stationCampaigns ?? STATION_CAMPAIGNS;
  const defaultCampaign = opts.defaultCampaign ?? DEFAULT_CAMPAIGN;
  const defaultLabel = clientLabel(CAMPAIGN_CLIENT[defaultCampaign]);
  const defaultShown = `${defaultLabel} (${campaignDisplay(defaultCampaign)})`;

  if (values.length === 0) return defaultPreselect(defaultCampaign, null);

  if (values.length > 1) {
    const shown = values.map((v) => JSON.stringify(v)).join(', ');
    return defaultPreselect(
      defaultCampaign,
      `The Hub sent more than one client (${shown}); that is not allowed, so nothing was preselected. Showing ${defaultShown}.`,
    );
  }

  const raw = values[0] ?? '';
  const key = raw.trim().toLowerCase();
  if (key === '') return defaultPreselect(defaultCampaign, null);

  if (!isClientKey(key)) {
    return defaultPreselect(
      defaultCampaign,
      `The Hub asked for ${JSON.stringify(raw)}, which is not a plan this station knows. Showing ${defaultShown}.`,
    );
  }

  const candidates = stationCampaigns.filter((c) => CAMPAIGN_CLIENT[c] === key);
  const label = CLIENT_LABELS[key];

  if (candidates.length === 1) {
    return { campaign: candidates[0]!, client: key, source: 'hub', notice: null };
  }
  if (candidates.length === 0) {
    return defaultPreselect(
      defaultCampaign,
      `The Hub asked for ${label}; this bench serves ${defaultShown} only today. Nothing was switched.`,
    );
  }
  return defaultPreselect(
    defaultCampaign,
    `The Hub asked for ${label}, which has ${candidates.length} campaigns at this bench. Nothing was picked automatically. Showing ${defaultShown}.`,
  );
}

/**
 * What the shell calls: the URL first, else the values remembered at boot, else the default.
 * A URL that carries the parameter always wins over the remembered copy.
 */
export function resolveFromLocation(search: string, store?: StorageLike, opts: PreselectOptions = {}): ClientPreselect {
  const fromUrl = hubClientValues(search);
  const values = fromUrl.length > 0 ? fromUrl : (recallHubClientValues(store) ?? []);
  return resolveClientPreselect(values, opts);
}
